import React, {Suspense} from 'react';
import {useNavigate} from 'react-router';
import {PermissionTypeKr, RoleType} from '@di-shared/api/di/type';
import {emitEventLog} from '@di-shared/features/analytics';
import {getHmcType} from '@di-shared/features/capital/capitalMessage';
import Header from '@di-shared/features/common/Header';
import HeaderCloseButton from '@di-shared/features/common/HeaderCloseButton';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import ScrollToTopOnMount from '@di-shared/features/common/ScrollToTopOnMount';
import useApiError from '@di-shared/features/common/useApiError';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import useFirebaseStore from '@di-shared/features/environment/firebaseStore';
import useAvailableComponentKr from '@di-shared/features/home/kr/useAvailableComponent.kr';
import {ROUTE_PATHS} from '@di-shared/routes';
import {BrandType} from '@shared/cci/features/const/environment';
import usePendingHandler from '@shared/usePendingHandler';
import {WebviewUtils} from '@shared/webviewUtils';

export interface CapitalEvent1PageProps {
  vin: string;
}

/**
 * 이벤트 종료 후 제거할것 EVENT2
 *
 * @param props
 * @constructor
 */
function CapitalEvent1Page(props: CapitalEvent1PageProps) {
  return (
    <div className="wrap">
      <ScrollToTopOnMount />
      <Header
        className="header_absolute"
        right={<HeaderCloseButton />}
      />
      <Suspense fallback={null}>
        <Inner {...props} />
      </Suspense>
    </div>
  );
}

export function getEvent2Url(vin: string) {
  return `https://mydata-app.hyundaicapital.com/mydata/main/index.html#/event/myasset-campaign-second?hmcType=${getHmcType()}&cbno=${vin}`;
}

const ERROR_MESSAGE = {
  part1: {
    [BrandType.HYUNDAI]: `대표 차량이 블루링크에 가입되어 있으며 안전운전점수 지원차량인 경우 차량의 주사용자에 한해 참여 가능합니다.\n\n* 대표 차량을 변경하고 싶으신가요?\n변경 방법: 마이현대 앱 ‘마이’ 탭 > ‘이용 가능한 차량’ 선택 > 참여조건에 해당하는 ‘내 차량’ 선택 > ‘대표 차량으로 설정’ 버튼 선택`,
    [BrandType.KIA]: `대표 차량이 기아 커넥트에 가입되어 있으며 안전운전점수 지원차량인 경우 차량의 주사용자에 한해 참여 가능합니다.\n\n* 대표 차량을 변경하고 싶으신가요?\n변경 방법: Kia app ‘마이카’ 탭 > ‘이용 가능한 차량’ 선택 > 참여조건에 해당하는 ‘내 차량’ 선택 > ‘대표 차량으로 설정’ 버튼 선택`,
    [BrandType.GENESIS]: `대표 차량이 제네시스 커넥티드 서비스에 가입되어 있으며 안전운전점수 지원차량인 경우 차량의 주사용자에 한해 참여 가능합니다.\n\n* 대표 차량을 변경하고 싶으신가요?\n변경 방법: MY GENESIS ‘마이카’ 탭 > ‘이용 가능한 차량’ 선택 > 참여조건에 해당하는 ‘내 차량’ 선택 > ‘대표 차량으로 설정’ 버튼 선택`,
  },
  part2: {
    [BrandType.HYUNDAI]: `대표 차량이 공유받은 차인 경우 참여가 불가합니다.\n\n* 대표 차량을 변경하고 싶으신가요?\n변경 방법: 마이현대 앱 ‘마이’ 탭 > ‘이용 가능한 차량’ 선택 > 참여조건에 해당하는 ‘내 차량’ 선택 > ‘대표 차량으로 설정’ 버튼 선택`,
    [BrandType.KIA]: `대표 차량이 공유받은 차인 경우 참여가 불가합니다.\n\n* 대표 차량을 변경하고 싶으신가요?\n변경 방법: Kia app ‘마이카’ 탭 > ‘이용 가능한 차량’ 선택 > 참여조건에 해당하는 ‘내 차량’ 선택 > ‘대표 차량으로 설정’ 버튼 선택`,
    [BrandType.GENESIS]: `대표 차량이 공유받은 차인 경우 참여가 불가합니다.\n\n* 대표 차량을 변경하고 싶으신가요?\n변경 방법: MY GENESIS ‘마이카’ 탭 > ‘이용 가능한 차량’ 선택 > 참여조건에 해당하는 ‘내 차량’ 선택 > ‘대표 차량으로 설정’ 버튼 선택`,
  },
};

function Inner({vin}: CapitalEvent1PageProps) {
  const brand = import.meta.env.VITE_BRAND as 'KIA' | 'HYUNDAI' | 'GENESIS';
  const {checkCapitalVinWhitelist} = useFirebaseStore(({checkCapitalVinWhitelist}) => ({checkCapitalVinWhitelist}));
  const prizeCarName = brand === BrandType.KIA ? 'The 2026 K8을' : brand === BrandType.HYUNDAI ? '팰리세이드를' : 'G80을';
  const prizeName = brand === BrandType.KIA ? '기아 The 2026 K8' : brand === BrandType.HYUNDAI ? '현대자동차 팰리세이드' : 'Genesis G80';
  const {role, getFeatureAgreed} = useAvailableComponentKr({vin});
  const {openAlert} = useCcWebviewUtilsHelper();
  const {resolveError} = useApiError();
  const navigate = useNavigate();

  const isCapitalAllAgreed =
    getFeatureAgreed(PermissionTypeKr.FINANCIAL_MANAGEMENT) && getFeatureAgreed(PermissionTypeKr.FINANCIAL_MANAGEMENT_3RD);

  const [join] = usePendingHandler(
    async () => {
      emitEventLog({
        event: 'di_vehicle_finance',
        data_action: 'event page ctr',
        data_name: '서비스 시작하고 이벤트 참여하기',
      });
      if (!(checkCapitalVinWhitelist(vin) || isEventDuration())) {
        await openAlert({
          title: '종료된 이벤트입니다.',
          message: '',
          okText: '확인',
        });
        WebviewUtils.close();
        return;
      }
      if (!isJoinable(role)) {
        const message = isEventPart2() ? ERROR_MESSAGE.part2[brand] : ERROR_MESSAGE.part1[brand];
        await openAlert({title: '이벤트 참여 대상이 아닙니다.', message});
        return;
      }

      if (!isCapitalAllAgreed) {
        await openAlert({
          title: '내 차 금융관리 서비스 가입 후 이벤트에 참여할 수 있어요!',
          message: '',
          okText: '서비스 가입하고 혜택 받을게요',
        });
        navigate(ROUTE_PATHS.CAPITAL_TERMS.getFullPath(vin) + '?from=event');
        return;
      }

      const nextScheme = `app://webView?isModalEntry=true&showNavigationBar=false&showToolbar=false&url=${encodeURIComponent(getEvent2Url(vin))}`;
      WebviewUtils.close(nextScheme);
    },
    async (error) => {
      await resolveError(error);
    }
  );

  return (
    <>
      <main
        id="content"
        className="content page_event"
      >
        <section className="section section_event">
          <span className="badge badge_container">마이데이터 럭키드로우 이벤트</span>

          {brand === BrandType.GENESIS ? (
            <h2 className="title">
              내 차 보험<span className="mid_dot"></span>대출 연결하고 <br />
              G80 당첨의 주인공이 되어 보세요.
            </h2>
          ) : (
            <h2 className="title">
              내 차 보험<span className="mid_dot"></span>대출 연동하고 <br />
              {prizeCarName} 잡아라!
            </h2>
          )}
          <div className="date_area">
            <span className="date_text">이벤트 기간</span>
            <p className="date_info">11월 5일 (수) - 12월 2일 (화)</p>
          </div>
          {brand === BrandType.GENESIS ? (
            <p className="event_info_desc">
              내 차 금융관리 서비스로
              <br />내 차량의 보험과 금융 정보를 연결해 보세요.
              <br />
              추첨을 통해 다양한 선물을 드립니다.
            </p>
          ) : (
            <p className="event_info_desc">
              지금 바로 '내 차 금융관리' 서비스를 시작하면
              <br />
              프리미엄세단부터 상품권까지 행운의 경품이 와르르
            </p>
          )}

          <div className="prize_area">
            <div className="event_imgbox">
              <ImageSelector
                className=""
                alt=""
                kiaLightSrc={() => import('@di-shared/assets/img/event/img_event_car_main_light.png')}
                kiaDarkSrc={() => import('@di-shared/assets/img/event/img_event_car_main.png')}
                hyundaiLightSrc={() => import('@di-shared/assets/img/event/hyundai/img_event_car_main_light.png')}
                hyundaiDarkSrc={() => import('@di-shared/assets/img/event/hyundai/img_event_car_main.png')}
                genesisLightSrc={() => import('@di-shared/assets/img/event/genesis/img_event_car_main.png')}
                genesisDarkSrc={() => import('@di-shared/assets/img/event/genesis/img_event_car_main.png')}
              />
            </div>
            <div className="prize_info">
              <span className="rank">1등 (1명)</span>
              <span className="prize_name">{prizeName}</span>
            </div>
          </div>
        </section>
        <section className="section section_prize">
          <div className="coupon_area">
            <div className="prize_box">
              <div className="prize_img">
                <ImageSelector
                  className=""
                  alt=""
                  kiaLightSrc={() => import('@di-shared/assets/img/event/img_coupon_01.png')}
                  kiaDarkSrc={() => import('@di-shared/assets/img/event/img_coupon_01.png')}
                  hyundaiLightSrc={() => import('@di-shared/assets/img/event/img_coupon_01.png')}
                  hyundaiDarkSrc={() => import('@di-shared/assets/img/event/img_coupon_01.png')}
                  genesisLightSrc={() => import('@di-shared/assets/img/event/img_coupon_01.png')}
                  genesisDarkSrc={() => import('@di-shared/assets/img/event/img_coupon_01.png')}
                />
              </div>
              <div className="prize_info">
                <span className="rank">2등 (5명)</span>
                <span className="prize_name">
                  {brand === BrandType.GENESIS ? '신세계상품권' : '신세계 상품권'}
                  <br />
                  50만원권
                </span>
              </div>
            </div>
            <div className="prize_box">
              <div className="prize_img">
                <ImageSelector
                  className=""
                  alt=""
                  kiaLightSrc={() => import('@di-shared/assets/img/event/img_coupon_02.png')}
                  kiaDarkSrc={() => import('@di-shared/assets/img/event/img_coupon_02.png')}
                  hyundaiLightSrc={() => import('@di-shared/assets/img/event/img_coupon_02.png')}
                  hyundaiDarkSrc={() => import('@di-shared/assets/img/event/img_coupon_02.png')}
                  genesisLightSrc={() => import('@di-shared/assets/img/event/img_coupon_02.png')}
                  genesisDarkSrc={() => import('@di-shared/assets/img/event/img_coupon_02.png')}
                />
              </div>
              <div className="prize_info">
                <span className="rank">3등 (10명)</span>
                <span className="prize_name">
                  {brand === BrandType.GENESIS ? '신세계상품권' : '신세계 상품권'}
                  <br />
                  20만원권
                </span>
              </div>
            </div>
            <div className="prize_box">
              <div className="prize_img">
                <ImageSelector
                  className=""
                  alt=""
                  kiaLightSrc={() => import('@di-shared/assets/img/event/img_coupon_03.png')}
                  kiaDarkSrc={() => import('@di-shared/assets/img/event/img_coupon_03.png')}
                  hyundaiLightSrc={() => import('@di-shared/assets/img/event/img_coupon_03.png')}
                  hyundaiDarkSrc={() => import('@di-shared/assets/img/event/img_coupon_03.png')}
                  genesisLightSrc={() => import('@di-shared/assets/img/event/img_coupon_03.png')}
                  genesisDarkSrc={() => import('@di-shared/assets/img/event/img_coupon_03.png')}
                />
              </div>
              {brand === BrandType.GENESIS ? (
                <div className="prize_info">
                  <span className="rank">4등 (100명)</span>
                  <span className="prize_name">
                    GS칼텍스
                    <br />
                    주유쿠폰 5만원권
                  </span>
                </div>
              ) : (
                <div className="prize_info">
                  <span className="rank">4등 (500명)</span>
                  <span className="prize_name">
                    GS칼텍스
                    <br />
                    주유쿠폰 5만원
                  </span>
                </div>
              )}
            </div>
            <div className="prize_box">
              <div className="prize_img">
                <ImageSelector
                  className=""
                  alt=""
                  kiaLightSrc={() => import('@di-shared/assets/img/event/img_coupon_03.png')}
                  kiaDarkSrc={() => import('@di-shared/assets/img/event/img_coupon_03.png')}
                  hyundaiLightSrc={() => import('@di-shared/assets/img/event/img_coupon_03.png')}
                  hyundaiDarkSrc={() => import('@di-shared/assets/img/event/img_coupon_03.png')}
                  genesisLightSrc={() => import('@di-shared/assets/img/event/img_coupon_03.png')}
                  genesisDarkSrc={() => import('@di-shared/assets/img/event/img_coupon_03.png')}
                />
              </div>
              {brand === BrandType.GENESIS ? (
                <div className="prize_info">
                  <span className="rank">5등 (500명)</span>
                  <span className="prize_name">
                    GS칼텍스
                    <br />
                    주유쿠폰 1만원권
                  </span>
                </div>
              ) : (
                <div className="prize_info">
                  <span className="rank">5등 (2000명)</span>
                  <span className="prize_name">
                    GS칼텍스
                    <br />
                    주유쿠폰 1만원
                  </span>
                </div>
              )}
            </div>
          </div>
        </section>
        <section className="section section_event_info">
          <div className="event_heading">
            <h3 className="title">내 차 금융관리 서비스란?</h3>
            {brand === BrandType.GENESIS ? (
              <p className="desc">자동차 보험, 운전자 보험부터 할부 금융 그리고 리스까지 내 차의 금융 정보를 한눈에 확인해 보세요.</p>
            ) : (
              <p className="desc">
                자동차 보험, 운전자 보험, 할부금융 그리고 리스까지
                <br />내 차의 금융 정보를 한번에 모아볼 수 있는 서비스예요!
              </p>
            )}
          </div>
          <div className="event_info_box">
            {brand === BrandType.GENESIS ? (
              <h4 className="title">
                흩어져있는 내 차량의 금융 정보를
                <br />
                MY GENESIS 앱에 연결하고
              </h4>
            ) : (
              <h4 className="title">
                흩어져있는 내 차의 보험, 대출
                <br />
                자산을 연결해보세요.
              </h4>
            )}

            <div className="event_img">
              <ImageSelector
                className=""
                alt=""
                kiaLightSrc={() => import('@di-shared/assets/img/event/event_info_01.png')}
                kiaDarkSrc={() => import('@di-shared/assets/img/event/event_info_01.png')}
                hyundaiLightSrc={() => import('@di-shared/assets/img/event/event_info_01.png')}
                hyundaiDarkSrc={() => import('@di-shared/assets/img/event/event_info_01.png')}
                genesisLightSrc={() => import('@di-shared/assets/img/event/event_info_01.png')}
                genesisDarkSrc={() => import('@di-shared/assets/img/event/event_info_01.png')}
              />
            </div>
          </div>
          <div className="event_info_box">
            {brand === BrandType.GENESIS ? (
              <h4 className="title">
                내가 가입한 자동차 보험과
                <br />
                운전자 보험의 정보부터
              </h4>
            ) : (
              <h4 className="title">
                내가 가입한 자동차 보험,
                <br />
                운전자 보험 정보와
              </h4>
            )}

            <div className="event_img">
              <ImageSelector
                className=""
                alt=""
                kiaLightSrc={() => import('@di-shared/assets/img/event/event_info_02.png')}
                kiaDarkSrc={() => import('@di-shared/assets/img/event/event_info_02.png')}
                hyundaiLightSrc={() => import('@di-shared/assets/img/event/event_info_02.png')}
                hyundaiDarkSrc={() => import('@di-shared/assets/img/event/event_info_02.png')}
                genesisLightSrc={() => import('@di-shared/assets/img/event/event_info_02.png')}
                genesisDarkSrc={() => import('@di-shared/assets/img/event/event_info_02.png')}
              />
            </div>
          </div>
          <div className="event_info_box">
            {brand === BrandType.GENESIS ? (
              <h4 className="title">
                이용 중인 할부 금융, 리스 현황과
                <br />
                납입 내역까지 한눈에 확인해 보세요
              </h4>
            ) : (
              <h4 className="title">
                이용 중인 할부금융, 리스의
                <br />
                현황과 납입내역을 확인해보세요.
              </h4>
            )}

            <div className="event_img">
              <ImageSelector
                className=""
                alt=""
                kiaLightSrc={() => import('@di-shared/assets/img/event/event_info_03.png')}
                kiaDarkSrc={() => import('@di-shared/assets/img/event/event_info_03.png')}
                hyundaiLightSrc={() => import('@di-shared/assets/img/event/event_info_03.png')}
                hyundaiDarkSrc={() => import('@di-shared/assets/img/event/event_info_03.png')}
                genesisLightSrc={() => import('@di-shared/assets/img/event/event_info_03.png')}
                genesisDarkSrc={() => import('@di-shared/assets/img/event/event_info_03.png')}
              />
            </div>
          </div>
          <p className="notice_refer">
            마이데이터 기반의 자산관리 서비스와 관련 제휴 이벤트는 제휴사 현대캐피탈을 통해 운영 및 제공됩니다.
          </p>

          <p className="notice_desc">
            광고심의필
            <br />
            광고 제작월 : 2025.10 <br />
            현대캐피탈 준법감사심의필 251030-002호(2025.10.30~2026.10.29)
          </p>
        </section>
      </main>
      <div className="floating_action_area">
        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          onClick={() => join()}
        >
          {brand === BrandType.GENESIS ? '이벤트 참여하기' : '서비스 시작하고 이벤트 참여하기'}
        </button>
      </div>
    </>
  );
}

function isJoinable(role: RoleType) {
  // 원앱 홈 배포 ~ 이벤트 종료 (9/24~9/30) : case 4인 경우 참여 불가 -> case 1,2,3만 참여 가능
  return role === RoleType.NON_CCS || role === RoleType.UNREGISTERED_SPEC || role === RoleType.PRIMARY_USER;
}

export function isEventDuration() {
  //  11/6 00:00:00 ~ 12/2 23:59:59
  const startDate = new Date(2025, 10, 5, 0, 0, 0, 0);
  const endDate = new Date(2025, 11, 2, 23, 59, 59, 999);
  const now = new Date();
  return now.getTime() >= startDate.getTime() && now.getTime() <= endDate.getTime();
}

function isEventPart2() {
  return new Date(2025, 8, 23, 23, 59, 59, 999).getTime() < Date.now();
}

export default CapitalEvent1Page;
